/**
 * The global Upgrades view: every upgrade the player owns in one dock (key Y,
 * or the pill by the timer). Kind filter chips with counts, a Ready and a
 * Not yet group of one-line rows, and the shared `UpgradeCard` with its
 * action for the selected row. Hovering a row rings what it affects on the
 * map (`onHighlight`). The list is `view/upgradesList.ts`.
 */
import { useEffect, useRef, useState } from 'react';
import type { Command } from '@/game/sim';
import { stockInUnits } from '@/game/view/resources';
import {
  KIND_ORDER,
  kindLabel,
  ownUpgrades,
  type UpgradeEntry,
  type UpgradeKind,
  upgradeCounts,
} from '@/game/view/upgradesList';
import { useSimFrame } from './app/simFrame';
import { DockPanel } from './hud/DockPanel';
import { UpgradeCard } from './UpgradeCard';
import { EntryAction, UpgradeRow, useTickMemo } from './UpgradeRows';

/** The player's upgrades; recomputed every `every` ticks (a closed pill needs only a rough count). */
export function useOwnUpgrades(every: number): UpgradeEntry[] {
  const { view, ctx, real, playerId, cityNumbers, names } = useSimFrame();
  return useTickMemo(
    () => ownUpgrades(view, ctx, playerId, real, cityNumbers, names),
    view.tick,
    every,
    playerId
  );
}

/** The mouse entry in the top bar: "⬆ Upgrades 3 ready". */
export function UpgradesPill({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const ready = upgradeCounts(useOwnUpgrades(8)).ready;
  return (
    <button
      type="button"
      aria-pressed={open}
      title="Upgrades (Y)"
      onClick={onToggle}
      className={`chip shrink-0 gap-1.5 transition ${open ? 'chip-selected' : 'hover:bg-fill-hover'}`}
    >
      <span aria-hidden="true">⬆</span>
      {/* The word only where there is room beside the Cities chip. */}
      <span className="sr-only min-[1100px]:not-sr-only">Upgrades</span>
      {ready > 0 && (
        <span className="tabular-nums text-accent">
          {ready} <span className="sr-only min-[1100px]:not-sr-only">ready</span>
        </span>
      )}
    </button>
  );
}

export function UpgradesDock({
  dispatch,
  onHighlight,
  onClose,
}: {
  dispatch: (c: Command) => void;
  onHighlight: (ids: readonly string[]) => void;
  onClose: () => void;
}) {
  const { view: state, playerId } = useSimFrame();
  const entries = useOwnUpgrades(1);
  // Never leave rings behind, however the dock closes (Y, Esc, ✕).
  const clear = useRef(onHighlight);
  clear.current = onHighlight;
  useEffect(() => () => clear.current([]), []);
  const [filter, setFilter] = useState<UpgradeKind | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const stock = stockInUnits(state, playerId);
  const counts = upgradeCounts(entries);
  const shown = entries.filter((e) => filter === 'all' || e.kind === filter);
  const selected = shown.find((e) => e.id === selectedId) ?? shown[0];
  const groups = [
    { label: 'Ready', rows: shown.filter((e) => e.canDo) },
    { label: 'Not yet', rows: shown.filter((e) => !e.canDo) },
  ];
  const chips: { key: UpgradeKind | 'all'; label: string; n: number }[] = [
    { key: 'all', label: 'All', n: counts.all },
    ...KIND_ORDER.map((k) => ({ key: k, label: kindLabel(k), n: counts.byKind[k] })),
  ];

  return (
    <DockPanel
      size="tall"
      title="Upgrades"
      kind="upgrades"
      chips={
        <span className="chip shrink-0 text-fg-subtle" title="Toggle with Y, close with Esc">
          Y
        </span>
      }
      onClose={onClose}
      tabs={
        <fieldset className="flex shrink-0 gap-1 rounded-full bg-fill p-0.5">
          <legend className="sr-only">Kind</legend>
          {chips.map((c) => (
            <label
              key={c.key}
              className={`chip flex-1 cursor-pointer justify-center py-0.5 text-xs tabular-nums transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent ${c.key === filter ? 'chip-selected' : 'bg-transparent text-fg-muted hover:bg-fill-hover'}`}
            >
              <input
                type="radio"
                name="upgrade-kind"
                className="sr-only"
                checked={c.key === filter}
                onChange={() => setFilter(c.key)}
              />
              {c.label} <span className="opacity-60">{c.n}</span>
            </label>
          ))}
        </fieldset>
      }
      footer={selected && <EntryAction entry={selected} stock={stock} dispatch={dispatch} />}
    >
      {shown.length === 0 ? (
        <p className="px-1 text-fg-subtle">Nothing to upgrade</p>
      ) : (
        <div className="@container min-h-0 flex-1 overflow-y-auto">
          <div className="grid gap-2 @lg:h-full @lg:grid-cols-[minmax(0,1fr)_17rem]">
            <div className="@lg:min-h-0 @lg:overflow-y-auto">
              {groups.map(
                (g) =>
                  g.rows.length > 0 && (
                    <div key={g.label} className="flex flex-col gap-0.5">
                      <p className="px-2 pt-1 text-[10px] uppercase tracking-wider text-fg-subtle">
                        {g.label} <span className="tabular-nums">{g.rows.length}</span>
                      </p>
                      {g.rows.map((e) => (
                        <UpgradeRow
                          key={e.id}
                          entry={e}
                          selected={e.id === selected?.id}
                          stock={stock}
                          onSelect={() => setSelectedId(e.id)}
                          onHighlight={onHighlight}
                        />
                      ))}
                    </div>
                  )
              )}
            </div>
            <div className="@lg:min-h-0 @lg:overflow-y-auto">
              {selected && (
                <UpgradeCard
                  key={selected.id}
                  preview={selected.preview}
                  externalAction
                  onHighlight={onHighlight}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </DockPanel>
  );
}
